// The app shows every date as dd/mm/yyyy (e.g. 02/10/2026) and a date with a
// time as dd/mm/yyyy HH:mm (24h). Use these helpers for anything shown on
// screen, in PDFs, CSVs and printed documents. <input type="date"> values and
// API request parameters stay YYYY-MM-DD.

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Turn a date input into a Date. A plain 'YYYY-MM-DD' string (or one with a
 * midnight time and no zone) is read as that calendar day in local time, so it
 * never shifts to the day before or after.
 * @param {Date|string|number} dateInput
 * @returns {Date|null}
 */
export const parseDate = (dateInput) => {
  if (dateInput === null || dateInput === undefined || dateInput === '') return null;
  if (dateInput instanceof Date) return isNaN(dateInput.getTime()) ? null : dateInput;
  if (typeof dateInput === 'string') {
    const s = dateInput.trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ]00:00(?::00(?:\.0+)?)?)?$/);
    if (m) {
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return isNaN(d.getTime()) ? null : d;
    }
  }
  const d = new Date(dateInput);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * dd/mm/yyyy, or '-' when there is no valid date.
 * @param {Date|string|number} dateInput
 * @returns {string}
 */
export const formatDateDMY = (dateInput) => {
  const d = parseDate(dateInput);
  if (!d) return '-';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

/** The app-wide date formatter: dd/mm/yyyy. */
export const formatDate = formatDateDMY;

/** Kept for backward compatibility: dd/mm/yyyy. */
export const formatDateLocale = formatDateDMY;

/**
 * dd/mm/yyyy HH:mm (24h), or '-' when there is no valid date.
 * @param {Date|string|number} dateInput
 * @param {object} [opts]
 * @param {boolean} [opts.includeSeconds=false]
 * @returns {string}
 */
export const formatDateTimeDMY = (dateInput, { includeSeconds = false } = {}) => {
  if (!dateInput && dateInput !== 0) return '-';
  const d = parseDate(dateInput);
  if (!d) return '-';
  const base = `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return includeSeconds ? `${base}:${pad2(d.getSeconds())}` : base;
};

/** The app-wide date-and-time formatter: dd/mm/yyyy HH:mm. */
export const formatDateTime = formatDateTimeDMY;

/**
 * Formerly "31 Jul 2026"; the app now shows dd/mm/yyyy everywhere.
 * @param {Date|string} dateInput
 * @returns {string}
 */
export const formatDateDMonY = formatDateDMY;

/**
 * The deduction day (1-31) a loan's dates were made from, for a loan that
 * does not store it: the day of its first deduction, or 31 (the last day of
 * every month) when its deductions fall on month-ends. The API's schedule
 * (utils/loanSchedule.js) turns the day back into the same dates.
 * @param {Date|string} startDate the first deduction
 * @param {Date|string} [dueDate] the last deduction
 * @returns {number|null}
 */
export const deductionDayFromDates = (startDate, dueDate) => {
  const start = parseDate(startDate);
  if (!start) return null;
  const dates = [start, parseDate(dueDate)].filter(Boolean);
  const monthEnd = (d) => d.getDate() === new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  // Every date on a month-end (30 April, 31 July): a month-end salary.
  if (dates.every(monthEnd)) return 31;
  // The day as chosen; a short month only ever moves it earlier.
  return Math.max(...dates.map((d) => d.getDate()));
};

/**
 * Demo reset interval for display: "30 days" when it is whole days,
 * otherwise "N hours".
 * @param {number} hours
 * @returns {string}
 */
export const formatResetInterval = (hours) => {
  const h = Number(hours) > 0 ? Number(hours) : 720;
  if (h % 24 === 0) {
    const days = h / 24;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  return `${h} hour${h === 1 ? '' : 's'}`;
};

/**
 * Countdown as HH:MM:SS, prefixed with whole days ("29d 23:59:59") once
 * it is a day or more. Returns `expiredLabel` when time is up.
 * @param {number} ms milliseconds remaining
 * @param {string} expiredLabel
 * @returns {string}
 */
export const formatCountdown = (ms, expiredLabel) => {
  if (!Number.isFinite(ms) || ms <= 0) return expiredLabel;
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  const clock = `${pad(h)}:${pad(m)}:${pad(s)}`;
  return d > 0 ? `${d}d ${clock}` : clock;
};
